<template>
  <div
    class="relative w-full flex-col items-center text-white bg-[#120F14] mt-[70px] pb-[80px] px-4 sm:px-6 lg:px-8"
  >
    <!-- Spinner while loading -->
    <div
      v-if="!productDetails"
      class="flex justify-center items-center min-h-[500px] w-full"
    >
      <div class="twist-spinner"></div>
    </div>

    <!-- Product content once loaded -->
    <div v-else class="w-full max-w-[1100px] mx-auto pt-8">

      <!-- Back Button -->
      <button
        @click="$router.back()"
        class="flex items-center gap-2 text-[#B9CACB] hover:text-[#00F0FF] transition-colors mb-8 group"
      >
        <svg class="w-5 h-5 transition-transform group-hover:-translate-x-1" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" d="M15 19l-7-7 7-7" />
        </svg>
        <span class="text-sm font-semibold uppercase tracking-wider">{{ $t('ProductDetails.BackToStore') }}</span>
      </button>

      <!-- Main Product Card -->
      <div class="bg-[#14181F] rounded-[16px] border border-gray-800/40 overflow-hidden">
        <div class="flex flex-col lg:flex-row">

          <!-- Left: Product Image -->
          <div class="relative w-full lg:w-[45%] bg-[#0D0B10] flex items-center justify-center p-8 lg:p-12 min-h-[300px] lg:min-h-[450px]">
            <img
              :src="productDetails.imageUrl || require('@/assets/store-fallback.png')"
              alt="Product Image"
              class="max-w-full max-h-[350px] object-contain drop-shadow-[0_10px_30px_rgba(0,0,0,0.5)]"
            />
            <!-- Top Header (Pill & Logo) -->
            <div class="absolute top-4 left-0 w-full px-4 flex justify-between items-center z-10 pointer-events-none">
              <!-- Status Badge -->
              <span v-if="productDetails.quantity > 0"
                class="text-[10px] font-bold uppercase tracking-wider px-3 py-1 rounded-full pointer-events-auto"
                :class="productDetails.quantity <= 3 ? 'bg-red-500/90 text-white' : 'bg-[#00F0FF]/90 text-black'">
                {{ productDetails.quantity <= 3 ? 'LIMITED' : 'IN STOCK' }}
              </span>
              <span v-else
                class="text-[10px] font-bold uppercase tracking-wider px-3 py-1 rounded-full bg-gray-600/90 text-white pointer-events-auto">
                SOLD OUT
              </span>
              
              <!-- Store Logo -->
              <img
                v-if="productDetails?.category"
                :src="
                  productDetails.category === '21.com' || productDetails.category === '21com'
                    ? require('@/assets/revamp/21com-logo.png')
                    : productDetails.category === 'kick'
                    ? require('@/assets/kick-logo.png')
                    : productDetails.category === '21com'
                    ? require('@/assets/revamp/21com-logo.png')
                    : ''
                "
                alt="Store Logo"
                class="object-contain w-12 h-12 pointer-events-auto"
              />
            </div>
          </div>

          <!-- Right: Product Details -->
          <div class="flex-1 p-6 sm:p-8 lg:p-10 flex flex-col justify-between">
            <!-- Top Section -->
            <div>
              <!-- Title -->
              <h1 class="text-2xl sm:text-3xl font-extrabold text-white font-oxanium tracking-tight mb-4">
                {{ productDetails.title }}
              </h1>

              <!-- Description -->
              <div class="text-[#B9CACB] text-sm sm:text-base leading-relaxed whitespace-pre-line mb-8 max-h-[200px] overflow-y-auto pr-2 scrollbar-thin">
                {{ productDetails.description }}
              </div>

              <!-- Stats Row -->
              <div class="flex flex-col sm:flex-row gap-4 mb-8">
                <!-- Points -->
                <div class="flex items-center gap-3 bg-[#1A1D24] rounded-[10px] border border-gray-700/50 px-5 py-4 flex-1">
                  <div class="w-10 h-10 rounded-full bg-[#0D0B10] flex items-center justify-center flex-shrink-0">
                    <svg class="w-5 h-5 text-[#00F0FF]" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
                    </svg>
                  </div>
                  <div class="flex flex-col">
                    <span class="text-[10px] font-bold text-gray-400 uppercase tracking-widest">{{ $t('ProductDetails.Price') }}</span>
                    <span class="text-xl font-extrabold text-[#00F0FF] font-oxanium leading-none mt-0.5">
                      {{ productDetails.requiredPoints.toLocaleString() }}
                      <span class="text-[10px] text-[#B9CACB] font-bold ml-1">{{ $t('ProductDetails.Pts') }}</span>
                    </span>
                  </div>
                </div>

                <!-- Quantity -->
                <div class="flex items-center gap-3 bg-[#1A1D24] rounded-[10px] border border-gray-700/50 px-5 py-4 flex-1">
                  <div class="w-10 h-10 rounded-full bg-[#0D0B10] flex items-center justify-center flex-shrink-0">
                    <svg class="w-5 h-5 text-[#beff2e]" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                    </svg>
                  </div>
                  <div class="flex flex-col">
                    <span class="text-[10px] font-bold text-gray-400 uppercase tracking-widest">{{ $t('ProductDetails.Available') }}</span>
                    <span class="text-xl font-extrabold text-[#beff2e] font-oxanium leading-none mt-0.5">
                      {{ productDetails.quantity.toLocaleString() }}
                      <span class="text-[10px] text-[#B9CACB] font-bold ml-1">{{ $t('ProductDetails.Left') }}</span>
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <!-- Bottom: Action -->
            <div class="flex flex-col gap-3">
              <!-- Verified Rule Info -->
              <div class="relative">
                <button
                  @click="showTooltip = !showTooltip"
                  class="flex items-center gap-2 text-xs text-[#B9CACB] hover:text-[#00F0FF] transition-colors"
                >
                  <div
                    class="w-5 h-5 flex items-center justify-center rounded-full border border-gray-600 text-gray-400 text-[10px] font-bold"
                  >
                    !
                  </div>
                  <span>{{ $t('ProductDetails.VerifiedPurchaseRule') }}</span>
                </button>

                <!-- Tooltip -->
                <transition name="fade">
                  <div
                    v-if="showTooltip"
                    class="absolute bottom-full mb-3 left-0 bg-[#1A1D24] border border-gray-700/50 text-white text-xs rounded-[10px] p-4 shadow-[0_10px_40px_rgba(0,0,0,0.5)] z-50 max-w-[340px]"
                  >
                    <div class="flex justify-between items-center gap-3 mb-2">
                      <span class="text-[#00F0FF] font-bold text-sm">{{ $t('ProductDetails.VerifiedRule') }}</span>
                    </div>
                    <p class="text-[#B9CACB] leading-relaxed">
                      {{ $t('ProductDetails.VerifiedRuleText') }}
                    </p>
                  </div>
                </transition>
              </div>

              <!-- Buy Button -->
              <button
                class="w-full py-4 bg-[#00F0FF] text-black font-extrabold text-base rounded-[10px] uppercase tracking-wider transition-all duration-300 hover:bg-[#00d4e0] hover:shadow-[0_0_20px_rgba(0,240,255,0.3)] disabled:opacity-40 disabled:cursor-not-allowed"
                @click="showConfirm(productDetails)"
                :disabled="loading || productDetails.quantity <= 0"
              >
                {{ productDetails.quantity <= 0 ? $t('ProductDetails.SoldOut') : $t('ProductDetails.BuyItem') }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Modals -->
    <StoreSuccessModal
      :showSuccessModal="showSuccessModal"
      :ticket="ticket"
      @closeSuccessModal="showSuccessModal = false"
    />

    <ErrorModal
      v-show="showErrorModal"
      :show="showErrorModal"
      :title="errorTitle"
      :message="errorMessage"
      @close="closeErrorModal"
    />

    <StoreConfirmationModal
      :showConfirmModal="showConfirmModal"
      :productName="selectedProductName"
      :requiredPoints="selectedProductPoints"
      :loading="loading"
      @confirmPurchase="confirmPurchase"
      @closeConfirmModal="showConfirmModal = false"
    />
  </div>
</template>

<script>
import axios from "axios";
import ErrorModal from "@/components/ErrorModal.vue";
import StoreConfirmationModal from "@/components/StoreConfirmationModal.vue";
import StoreSuccessModal from "@/components/StoreSuccessModal.vue";

export default {
  components: {
    ErrorModal,
    StoreConfirmationModal,
    StoreSuccessModal,
  },
  data() {
    return {
      // General
      loading: false,
      points: 0,
      animatedPoints: 0,
      showFallback: true,

      // Product
      products: [],
      productDetails: null,
      transactions: [],
      showTooltip: false,

      // Purchase
      selectedProductId: null,
      selectedProductName: "",
      selectedProductPoints: 0,

      // Filters & Sorting
      searchQuery: "",
      selectedCategory: "All",
      selectedSortOption: "lowToHigh",
      sortOptions: [
        { label: "From Low to High", value: "lowToHigh" },
        { label: "From High to Low", value: "highToLow" },
        { label: "Alphabetically (A–Z)", value: "aToZ" },
        { label: "Alphabetically (Z–A)", value: "zToA" },
      ],
      selectedPointsRange: "",
      minPoints: "",
      maxPoints: "",
      showSortDropdown: false,
      showDesktopFilter: false,
      showMobileFilter: false,

      // Pagination
      currentPage: 1,
      itemsPerPage: 8,
      animatedTotalItems: 0,

      // UI Modals
      showErrorModal: false,
      showSuccessModal: false,
      showConfirmModal: false,
      showRedirectModal: false,
      showTransactionModal: false,

      // Error/Success Modal Content
      errorTitle: "",
      errorMessage: "",

      // Account extras
      hasDiscord: false,
    };
  },
  computed: {
    isUserLoggedIn() {
      return this.$store.state.user !== null && this.$store.state.isVerified;
    },
    needsProfileUpdate() {
      const user = this.$store.state.user;
      return user && (!user.stake || !user.kick);
    },
    filteredProducts() {
      let filtered = this.products.filter((product) => product.status === "active");

      if (this.searchQuery) {
        filtered = filtered.filter((product) =>
          product.title.toLowerCase().includes(this.searchQuery.toLowerCase())
        );
      }

      if (this.selectedCategory && this.selectedCategory.toLowerCase() !== "all") {
        filtered = filtered.filter(
          (product) =>
            product.category &&
            product.category.toLowerCase() === this.selectedCategory.toLowerCase()
        );
      }

      const min = this.minPoints !== "" ? Number(this.minPoints) : 0;
      const max = this.maxPoints !== "" ? Number(this.maxPoints) : Infinity;

      filtered = filtered.filter(
        (product) => product.requiredPoints >= min && product.requiredPoints <= max
      );

      return filtered;
    },
    paginatedProducts() {
      const start = (this.currentPage - 1) * this.itemsPerPage;
      return this.filteredProducts.slice(start, start + this.itemsPerPage);
    },
    totalItems() {
      return this.filteredProducts.length;
    },
    totalPages() {
      return Math.ceil(this.totalItems / this.itemsPerPage);
    },
    itemStart() {
      return this.totalItems === 0 ? 0 : (this.currentPage - 1) * this.itemsPerPage + 1;
    },
    itemEnd() {
      return Math.min(this.currentPage * this.itemsPerPage, this.totalItems);
    },
    visiblePages() {
      const range = [];
      let start = Math.max(1, this.currentPage - 2);
      let end = Math.min(this.totalPages, this.currentPage + 2);

      for (let i = start; i <= end; i++) range.push(i);
      return range;
    },
  },
  watch: {
    selectedSortOption(newVal) {
      const match = this.sortOptions.find((opt) => opt.value === newVal);
      this.selectedSortLabel = match ? match.label : "";
    },
    selectedCategory() {
      this.currentPage = 1;
    },
    totalItems(newValue) {
      this.animateNumber("animatedTotalItems", newValue);
    },
  },
  methods: {
    async fetchProductDetails() {
      const productId = this.$route.params.id;
      const baseUrl = process.env.VUE_APP_BASE_API;

      try {
        const response = await axios.get(`${baseUrl}/api/store/products/${productId}`, {
          headers: {
            "x-api-key": process.env.VUE_APP_X_API_KEY,
          },
        });

        this.productDetails = response.data.product;
      } catch (error) {
        console.error("Error fetching product details:", error);
        this.showError("❌ Failed to load product.");
      }
    },
    async purchaseProduct(productId) {
      const baseUrl = process.env.VUE_APP_BASE_API;
      const token = localStorage.getItem("maherco-access-token");

      if (!token) {
        this.showError("You must be logged in to make a purchase.");
        throw new Error("Not authenticated");
      }

      try {
        const response = await axios.post(
          `${baseUrl}/api/store/purchase`,
          { productId },
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "x-api-key": process.env.VUE_APP_X_API_KEY,
            },
          }
        );

        const ticket = response.data?.ticket;

        if (response.status === 200 && ticket) {
          this.showSuccess(ticket);
          this.fetchProductDetails();
        } else {
          this.showError("❌ Purchase succeeded but no ticket was returned.");
        }
      } catch (error) {
        if (error.response) {
          const { status, data } = error.response;
          if (status === 402) {
            this.showError("⚠️ Insufficient points to purchase this product.");
          } else {
            this.showError(`❌ ${data.message || "Unexpected error."}`);
          }
        } else {
          this.showError("❌ Failed to process purchase. Please try again.");
        }
        throw error;
      }
    },
    async fetchUserPoints() {
      const baseUrl = process.env.VUE_APP_BASE_API;
      const token = localStorage.getItem("maherco-access-token");

      if (!token) {
        console.error("User not authenticated.");
        return;
      }

      try {
        const response = await axios.get(`${baseUrl}/api/users/me/points`, {
          headers: {
            Authorization: `Bearer ${token}`,
            "x-api-key": process.env.VUE_APP_X_API_KEY,
          },
        });

        this.points = response.data.points;
        this.animateValues();
      } catch (error) {
        console.error("Error fetching user points:", error);
      }
    },
    async confirmPurchase() {
      if (this.loading) return;
      if (!this.selectedProductId) {
        this.showError("❌ No product selected.");
        return;
      }

      this.loading = true;
      try {
        await this.purchaseProduct(this.selectedProductId);
        this.showConfirmModal = false;
      } catch (error) {
        // already handled inside purchaseProduct
      } finally {
        this.loading = false;
      }
    },
    showSuccess(ticket) {
      this.ticket = ticket;
      this.showErrorModal = false;
      this.showConfirmModal = false;
      this.showRedirectModal = false;
      this.showTransactionModal = false;
      this.showSuccessModal = true;
    },
    showError(message, title = "Oops! Something went wrong") {
      this.errorMessage = message;
      this.errorTitle = title;
      this.showSuccessModal = false;
      this.showConfirmModal = false;
      this.showRedirectModal = false;
      this.showTransactionModal = false;
      this.showErrorModal = true;
    },
    showConfirm(product) {
      this.selectedProductId = product._id;
      this.selectedProductName = product.title;
      this.selectedProductPoints = product.requiredPoints;

      this.showSuccessModal = false;
      this.showErrorModal = false;
      this.showRedirectModal = false;
      this.showTransactionModal = false;
      this.showConfirmModal = true;
    },
    closeErrorModal() {
      this.showErrorModal = false;
      this.errorMessage = "";
      this.errorTitle = "";
    },
    animateValues() {
      this.animateNumber("animatedPoints", this.points);
    },
    animateNumber(variable, targetValue) {
      let startValue = this[variable] || 0;
      if (startValue === targetValue) return;

      const step = Math.max(1, Math.abs(targetValue - startValue) / 20);
      const isNegative = startValue > targetValue;

      const interval = setInterval(() => {
        if (isNegative) {
          startValue -= step;
          if (startValue <= targetValue) {
            startValue = targetValue;
            clearInterval(interval);
          }
        } else {
          startValue += step;
          if (startValue >= targetValue) {
            startValue = targetValue;
            clearInterval(interval);
          }
        }

        this[variable] = Math.round(startValue).toLocaleString();
      }, 50);
    },
    toggleDesktopFilter() {
      this.showDesktopFilter = !this.showDesktopFilter;
      console.log("desktop dropdown working");
    },
    toggleMobileFilter() {
      this.showMobileFilter = !this.showMobileFilter;
      console.log("mobile dropdown working");
    },
    toggleSortDropdown() {
      this.showSortDropdown = !this.showSortDropdown;
    },
    applyFilter() {
      this.currentPage = 1;
    },
    applySort(option) {
      this.selectedSortOption = option.value;
      this.sortProducts(option.value);
      this.showSortDropdown = false;
    },
    sortProducts(order) {
      if (order === "highToLow") {
        this.products.sort((a, b) => b.requiredPoints - a.requiredPoints);
      } else if (order === "lowToHigh") {
        this.products.sort((a, b) => a.requiredPoints - b.requiredPoints);
      } else if (order === "aToZ") {
        this.products.sort((a, b) => a.title.localeCompare(b.title));
      } else if (order === "zToA") {
        this.products.sort((a, b) => b.title.localeCompare(a.title));
      }
    },
    goToPage(page) {
      if (page >= 1 && page <= this.totalPages) {
        this.currentPage = page;
      }
    },
    prevPage() {
      if (this.currentPage > 1) {
        this.currentPage--;
      }
    },
    nextPage() {
      if (this.currentPage < this.totalPages) {
        this.currentPage++;
      }
    },
    prevActivePage() {
      this.prevPage();
    },
    nextActivePage() {
      this.nextPage();
    },
  },
  mounted() {
    this.fetchUserPoints();

    if (this.$route.params.id) {
      this.fetchProductDetails();
    }
  },
};
</script>

<style>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
